import React, { useContext, useEffect, useState } from 'react';
import styles from './articlesPanel.module.scss';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import findIndex from 'lodash/findIndex';
import { KibTabsNew, KibTabsNewItem } from '@chewy/kib-content-groups-react';
import '@chewy/kib-content-groups-styles/dist/styles/kib-tabs-new.css';
import { navigateToArticle } from '@/utils/helpCenterUtils';
import { useRouter } from 'next/navigation';
import OHCContext from '@/providers/OHCContext';
import { useTrack } from '@chewy/use-analytics';
import ArticleContentPanel, { Article } from './articleContentPanel';
import useClientMetadata from '@/hooks/useClientMetadata';

export interface ArticlesPanelProps {
  readonly articles?: Article[] | null;
  readonly selectedArticle?: Article | null;
  readonly isDesktopMode?: boolean;
}

const ArticlesPanel: React.FC<ArticlesPanelProps> = ({ articles, selectedArticle, isDesktopMode = false }) => {
  const [selectedArticleIndex, setSelectedArticleIndex] = useState(0);
  const { setSelectedArticle, moreHelpLinksData, setMoreHelpLinksData, exploreChewyLinksData, isMobileApp } = useContext(OHCContext) || {
    moreHelpLinksData: [],
    exploreChewyLinksData: [],
    isMobileApp: false
  };
  const router = useRouter();
  const track = useTrack();
  useClientMetadata(selectedArticle?.id);

  const handleClick = (_event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!articles || articles.length === 0) {
      return;
    }

    if (index < 0 || index >= articles.length) {
      return;
    }

    const obj: Article | undefined = articles[index];

    const eventProperties = {
      id: 'articles-panel-article-button-id',
      parentId: 'articles-panel-id',
      level: 'articles-panel',
      type: 'static',
      position: 1,
      event_name: 'Link Clicked',
      eventAction: 'click',
      eventCategory: 'help-center-articles-tabs',
      eventLabel: 'help-center-articles-tabs-tab-click',
      name: 'Articles Tabs Tab Clicked',
      articleId: obj?.id,
      articleTitle: obj?.title,
      articleCategoryId: obj?.categoryId,
      sectionId: obj?.sectionId,
      sectionTitle: obj?.sectionTitle,
      sectionCategoryId: obj?.sectionCategoryId,
      topicId: obj?.topicId,
      topicTitle: obj?.topicTitle,
      topicCategoryId: obj?.topicCategoryId
    };
    track('Articles Panel Article Tab Clicked', eventProperties);

    setSelectedArticle(obj || null);
    navigateToArticle(obj?.id, router);
  };

  useEffect(() => {
    if (!articles || articles.length === 0) {
      return;
    }

    if (selectedArticle) {
      const idx = findIndex(articles, article => article?.id === selectedArticle?.id);
      setSelectedArticleIndex(idx >= 0 ? idx : 0);
    } else {
      setSelectedArticleIndex(0);
    }

    const tmpSelectedArticle = selectedArticle || articles[0];

    track('Articles Panel Article Header Viewed', {
      id: 'articles-panel-article-button-id',
      parentId: 'articles-panel-id',
      level: 'articles-panel',
      type: 'static',
      position: 1,
      event_name: 'Header Viewed',
      eventAction: 'impression',
      eventCategory: 'help-center-articles',
      eventLabel: 'help-center-article-header-view',
      name: `Article Header Viewed: ${tmpSelectedArticle?.title}`,
      articleId: tmpSelectedArticle?.id,
      articleTitle: tmpSelectedArticle?.title,
      articleCategoryId: tmpSelectedArticle?.categoryId
    });

    if (!isEmpty(selectedArticle?.relatedArticles)) {
      const relatedArticlesObj = map(selectedArticle?.relatedArticles, article => ({
        id: article?.id,
        title: article?.title,
        icon: 'GreenIcon'
      }));
      setMoreHelpLinksData(relatedArticlesObj);
    } else {
      setMoreHelpLinksData([]);
    }
  }, [articles, selectedArticle, setMoreHelpLinksData, track]);

  return (
    <div role="region" aria-label="Article Content">
      {isMobileApp ? (
        <div className={styles.mobileArticleContentPanel}>
          <ArticleContentPanel article={selectedArticle} isDesktopMode={isDesktopMode} moreHelpLinksData={moreHelpLinksData} exploreChewyLinksData={exploreChewyLinksData} />
        </div>
      ) : (
        <KibTabsNew
          className={styles.tabsRoot}
          onTabChange={function Xs(event, index) {
            if (index !== undefined) {
              handleClick(event, index);
            }
          }}
          selected={selectedArticleIndex}
          aria-label="Articles tabs"
        >
          {map(articles, article => {
            if (!article) {
              return (
                <div className={styles.noContent}>
                  <p>No content available for this article.</p>
                </div>
              );
            }
            return (
              <KibTabsNewItem
                data-testid={`article-tab-${article?.id}`}
                key={article?.id}
                id={article?.id || undefined}
                label={article?.title || undefined}
                aria-label={article?.title ? `Article: ${article.title}` : undefined}
              >
                <ArticleContentPanel article={article} isDesktopMode={isDesktopMode} moreHelpLinksData={moreHelpLinksData} exploreChewyLinksData={exploreChewyLinksData} />
              </KibTabsNewItem>
            );
          })}
        </KibTabsNew>
      )}
    </div>
  );
};

export default ArticlesPanel;
